import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Cloud, CheckCircle2, ExternalLink, Loader2, RefreshCw, Unplug,
  Building2, AlertTriangle, ChevronRight,
} from "lucide-react";
import { useFicStatus } from "@/context/FicStatusContext";

export default function FicIntegration() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh: refreshGlobalFic } = useFicStatus();
  const [status, setStatus] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [refreshingCompanies, setRefreshingCompanies] = useState(false);
  const [selectingId, setSelectingId] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fic/status");
      setStatus(data);
      // Keep global context (used by sidebar badge + InvoiceReview button) in sync
      refreshGlobalFic();
      if (data.connected) {
        try {
          const { data: c } = await api.get("/fic/companies");
          setCompanies(c.companies || []);
        } catch (e) {
          toast.error("Impossibile leggere le aziende FIC. Riconnetti l'account.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Handle post-OAuth redirect: /app/integrations?fic=connected|error
    const params = new URLSearchParams(location.search);
    const fic = params.get("fic");
    if (fic === "connected") {
      toast.success("Fatture in Cloud collegato con successo");
      navigate("/app/integrations", { replace: true });
    } else if (fic === "error") {
      const reason = params.get("reason") || "unknown";
      toast.error(`Connessione a Fatture in Cloud fallita: ${reason}`);
      navigate("/app/integrations", { replace: true });
    }
    loadStatus();
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/fic/auth-url");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile avviare la connessione");
      setConnecting(false);
    }
  };

  const refreshCompanies = async () => {
    setRefreshingCompanies(true);
    try {
      const { data } = await api.get("/fic/companies");
      setCompanies(data.companies || []);
      toast.success("Elenco aziende aggiornato");
    } catch (e) {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setRefreshingCompanies(false);
    }
  };

  const selectCompany = async (c) => {
    setSelectingId(c.id);
    try {
      await api.put("/fic/company", { company_id: c.id, company_name: c.name });
      toast.success(`Azienda "${c.name}" selezionata`);
      loadStatus();
    } catch (e) {
      toast.error("Errore nella selezione");
    } finally {
      setSelectingId(null);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare Fatture in Cloud? Dovrai autorizzarlo di nuovo per inviare fatture.")) return;
    try {
      await api.post("/fic/disconnect");
      toast.success("Disconnesso");
      setCompanies([]);
      loadStatus();
    } catch (e) {
      toast.error("Errore");
    }
  };

  const connected = !!status?.connected;
  const configured = !!status?.configured;

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="integrations-page">
      {/* Breadcrumb */}
      <nav className="text-xs text-zinc-500 mb-4" data-testid="breadcrumbs">
        <Link to="/app" className="hover:text-zinc-900">Home</Link>
        <span className="mx-1.5">/</span>
        <span className="text-zinc-900">Integrazioni</span>
      </nav>

      <div className="mb-8 flex items-start gap-3">
        <div className="w-11 h-11 rounded-md bg-blue-50 grid place-items-center shrink-0">
          <Cloud className="w-5 h-5 text-[#1D4ED8]" />
        </div>
        <div>
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tight">Integrazioni</h1>
          <p className="text-zinc-600 mt-1 text-sm">
            Collega servizi esterni per inviare le autofatture direttamente allo SDI.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-[#E8E4D9] bg-white p-16 grid place-items-center">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
        </div>
      ) : (
        <FicCard
          configured={configured}
          connected={connected}
          status={status}
          companies={companies}
          onConnect={connect}
          onDisconnect={disconnect}
          onSelectCompany={selectCompany}
          onRefreshCompanies={refreshCompanies}
          selectingId={selectingId}
          refreshingCompanies={refreshingCompanies}
          connecting={connecting}
        />
      )}

      <p className="text-xs text-zinc-500 mt-6 leading-relaxed max-w-2xl">
        <strong>Come funziona.</strong> Fatture in Cloud è un intermediario certificato. Quando premi
        &quot;Invia a Fatture in Cloud&quot; su una fattura, ricreiamo un&apos;autofattura equivalente nel loro
        sistema (tipo TD17/TD18/TD19) e la inoltriamo all&apos;Agenzia delle Entrate tramite il loro canale
        SDI. Puoi sempre effettuare prima un test in modalità &quot;dry run&quot; senza inviare realmente.
      </p>
    </div>
  );
}

function FicCard({
  configured, connected, status, companies, onConnect, onDisconnect,
  onSelectCompany, onRefreshCompanies, selectingId, refreshingCompanies, connecting,
}) {
  return (
    <div className="rounded-xl border border-[#E8E4D9] bg-white overflow-hidden shadow-sm" data-testid="fic-card">
      <div className="p-6 flex items-start gap-4 border-b border-[#E8E4D9]/70">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-50 to-emerald-50 grid place-items-center shrink-0 border border-blue-100">
          <Cloud className="w-6 h-6 text-[#1D4ED8]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-semibold text-lg">Fatture in Cloud</div>
            {connected ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium" data-testid="fic-status-badge">
                <CheckCircle2 className="w-3 h-3" /> Connesso
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F5F2EA] text-zinc-700 border border-[#E8E4D9] text-xs font-medium" data-testid="fic-status-badge">
                Non connesso
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-600 mt-1">
            Invia direttamente allo SDI le autofatture generate. Richiede un account Fatture in Cloud
            attivo con e-invoicing abilitato.
          </p>
        </div>
        <a
          href="https://www.fattureincloud.it"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex items-center gap-1 text-xs text-[#1D4ED8] hover:underline whitespace-nowrap"
          data-testid="fic-website-link"
        >
          <ExternalLink className="w-3 h-3" /> fattureincloud.it
        </a>
      </div>

      {!configured && (
        <div className="p-6 bg-amber-50/50 border-b border-amber-200 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            L&apos;integrazione non è configurata sul server (mancano <code>FIC_CLIENT_ID / SECRET</code>).
            Contatta il supporto.
          </div>
        </div>
      )}

      {configured && !connected && (
        <div className="p-6 grid place-items-center text-center">
          <div className="mb-4 text-sm text-zinc-600 max-w-md">
            Collega il tuo account Fatture in Cloud per abilitare l&apos;invio automatico allo SDI.
            Ti verrà richiesta l&apos;autorizzazione tramite OAuth 2.0.
          </div>
          <button
            onClick={onConnect}
            disabled={connecting}
            data-testid="fic-connect-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#1D4ED8] text-white font-semibold text-sm hover:bg-[#1E3A8A] shadow-sm shadow-blue-600/20 disabled:opacity-70 transition-colors"
          >
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
            Collega Fatture in Cloud
          </button>
        </div>
      )}

      {configured && connected && (
        <div className="p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-medium mb-1">Azienda attiva</div>
              <div className="font-display font-semibold text-lg" data-testid="fic-selected-company">
                {status?.selected_company_name || (
                  <span className="text-amber-700 text-sm">Nessuna azienda selezionata</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onRefreshCompanies}
                disabled={refreshingCompanies}
                data-testid="fic-refresh-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E8E4D9] text-sm hover:bg-[#F5F2EA] disabled:opacity-70 transition-colors"
              >
                {refreshingCompanies ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Aggiorna
              </button>
              <button
                onClick={onDisconnect}
                data-testid="fic-disconnect-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-red-600 border border-red-200 bg-red-50 text-sm hover:bg-red-100 transition-colors"
              >
                <Unplug className="w-3.5 h-3.5" /> Scollega
              </button>
            </div>
          </div>

          <div className="text-xs uppercase tracking-wider text-zinc-500 font-medium mb-2">Le tue aziende su FIC</div>
          {companies.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[#E8E4D9] p-6 text-center text-sm text-zinc-500">
              Nessuna azienda trovata. Se hai appena creato un&apos;azienda, premi &quot;Aggiorna&quot;.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="fic-companies-list">
              {companies.map((c) => {
                const isActive = status?.selected_company_id === c.id;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => !isActive && onSelectCompany(c)}
                      disabled={selectingId === c.id}
                      data-testid={`fic-company-${c.id}`}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                        isActive
                          ? "border-blue-500 bg-blue-50/50"
                          : "border-[#E8E4D9] hover:border-zinc-400 hover:bg-[#F5F2EA]"
                      }`}
                    >
                      <div className="w-9 h-9 rounded-md bg-[#F5F2EA] grid place-items-center shrink-0">
                        <Building2 className="w-4 h-4 text-zinc-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-zinc-900 truncate">{c.name}</div>
                        <div className="text-xs text-zinc-500 font-mono truncate">
                          ID {c.id}{c.tax_code ? ` · ${c.tax_code}` : ""}{c.type ? ` · ${c.type}` : ""}
                        </div>
                      </div>
                      {selectingId === c.id ? (
                        <Loader2 className="w-4 h-4 animate-spin text-[#1D4ED8]" />
                      ) : isActive ? (
                        <CheckCircle2 className="w-4 h-4 text-[#1D4ED8]" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-zinc-400" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
